// ===========================================================================
// Shared — patientSearchService.
//
// Búsqueda de pacientes por nombre y/o fecha de nacimiento para el componente
// transversal PatientSearch. Se apoya en el endpoint canónico del Patient Map
// V2 (`GET /api/PatientInfoV2/patient-map`), que ya acepta `memberFullName`
// (LIKE '%valor%') + `dateOfBirth` (match exacto YYYY-MM-DD) y devuelve la
// ficha completa del paciente — así, al seleccionar uno del dialog, ya viene
// toda su información sin una segunda llamada.
//
// PROVISIONAL: hoy se reutiliza getPatientMapV2. Cuando exista el endpoint
// dedicado de búsqueda (en construcción por backend), este service es el ÚNICO
// punto a cambiar: reemplazar la llamada de adentro de searchPatients por la
// del nuevo endpoint y mapear su respuesta al shape { patients, hasMore }. Los
// componentes (PatientSearch / PatientResultsDialog) no se tocan.
// ===========================================================================
import { getPatientMapV2 } from "../../../utils/services/Patients/getPatientMapV2";

/**
 * Busca pacientes similares por nombre y/o fecha de nacimiento.
 *
 * Debe enviarse al menos uno de `name` o `dateOfBirth`. Internamente llama a
 * `getPatientMapV2`, que ejecuta `patinfo.SP_GET_PATIENT_MAP`.
 *
 * @param {Object} params
 * @param {string|null} [params.name]          - nombre completo o parcial (LIKE '%valor%', enviado en mayúsculas por el SP)
 * @param {string|null} [params.dateOfBirth]   - "YYYY-MM-DD". Match exacto sobre DAT_DOB.
 * @param {number|null} [params.locationId]    - filtro opcional por location
 * @param {number} [params.pageSize=25]        - máximo de resultados a traer
 *
 * Ejemplo de llamada:
 * GET /api/PatientInfoV2/patient-map?memberFullName=GARCIA&dateOfBirth=1976-04-11&pageNumber=1&pageSize=25
 *
 * Respuesta (normalizada por este servicio):
 * {
 *   "patients": [
 *     {
 *       "cipId": "43750135999",
 *       "memberType": "ACA",
 *       "memberId": "002W24287",
 *       "memberFullName": "DRAYTON, SHERRI A",
 *       "dateOfBirth": "1968-07-11T00:00:00",
 *       "age": 57,
 *       "gender": "F",
 *       "email": "SDRAY711@AOL.COM",
 *       "phoneNumber": "",
 *       "mobileNumber": "(954) 806-7347",
 *       "status": 1,
 *       "pcpName": "Orly Pena",
 *       "pcpTitle": "PCP",
 *       "locationId": 11,
 *       "medicalCenter": "Stuart",
 *       "locationAddress": "1050 SE MONTEREY RD STE 201, Stuart, FL - 34994, Martin",
 *       "careGaps": 0
 *     }
 *   ],
 *   "hasMore": false
 * }
 *
 * @returns {Promise<{patients: Array, hasMore: boolean}>}
 * @throws si no se envía ni `name` ni `dateOfBirth`, o si la request falla.
 */
export const searchPatients = async ({ name, dateOfBirth, locationId, pageSize = 25 } = {}) => {
  const trimmedName = (name || "").trim();
  if (!trimmedName && !dateOfBirth) {
    throw new Error("Provide a patient name or a date of birth to search.");
  }

  const res = await getPatientMapV2({
    memberFullName: trimmedName || undefined,
    dateOfBirth: dateOfBirth || undefined,
    locationId: locationId ?? undefined,
    pageNumber: 1,
    pageSize,
  });

  const patients = Array.isArray(res?.data) ? res.data : [];
  // El SP no devuelve total; si llenamos la página, probablemente hay más.
  return { patients, hasMore: patients.length >= pageSize };
};
